/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { RowSelectionState } from "@tanstack/table-core";
import { Else, If, Then } from "react-if";
import { useUpdateEffect } from "react-use";
import { Box, FlexContainer } from "@nordcloud/gnui";
import { ApiKeysSortFields, SortOrder, ApiKey } from "~/generated/graphql";
import { LoaderWrap } from "~/components/Loader";
import { NoData } from "~/components/NoData";
import { Pagination } from "~/components/Pagination";
import { SortSelector } from "~/components/SortSelector";
import { TableWrapper } from "~/components/Tables";
import { TABLE_LABELS } from "~/constants";
import { useQueryState } from "~/hooks";
import { isEmpty, isNotNil } from "~/tools";
import { apiKeysColumns } from "~/views/permissions/APIKeys/components/ApiKeysTableColumns";
import { useGetApiKeys } from "../hooks/useGetApiKeys/useGetApiKeys";
import { QuerySort } from "../types/QuerySort";

const DELAY_PERIOD = 1000;

const sortOptions = [
  {
    value: ApiKeysSortFields.Name,
    label: "API Key Name",
  },
  {
    value: ApiKeysSortFields.CreatedAt,
    label: TABLE_LABELS.createdAt,
  },
  {
    value: ApiKeysSortFields.UpdatedAt,
    label: TABLE_LABELS.updatedAt,
  },
  {
    value: ApiKeysSortFields.UpdatedBy,
    label: TABLE_LABELS.updatedBy,
  },
];

type Props = {
  apiKeyCreateTrigger: number;
};

export function ApiKeysTable({ apiKeyCreateTrigger }: Props) {
  const { queryState, updateQueryState } = useQueryState<QuerySort>();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const handleSortDirection = (order: SortOrder) => {
    updateQueryState({
      ...queryState,
      order,
    });
  };

  const handleSortField = (field: ApiKeysSortFields) => {
    updateQueryState({
      ...queryState,
      field,
    });
  };

  const { data, loading, count, refetch } = useGetApiKeys({
    page: queryState.page,
    limit: queryState.limit,
    sort: {
      field: queryState.field ?? ApiKeysSortFields.Name,
      order: queryState.order ?? SortOrder.Asc,
    },
  });

  const refetchApiKeys = () => {
    setTimeout(() => {
      refetch();
    }, DELAY_PERIOD);
  };

  useUpdateEffect(() => {
    refetchApiKeys();
  }, [apiKeyCreateTrigger]);

  return (
    <>
      <Box boxStyle="lightGrey" spacing="spacing04">
        <FlexContainer justifyContent="space-between">
          <SortSelector<ApiKeysSortFields>
            options={sortOptions}
            value={queryState.field ?? ApiKeysSortFields.Name}
            direction={queryState.order ?? SortOrder.Asc}
            onSelect={handleSortField}
            onSortChange={handleSortDirection}
          />
        </FlexContainer>
      </Box>
      <LoaderWrap loading={loading} inContent>
        <If condition={isEmpty(data)}>
          <Then>
            <NoData hasIcon message={"There are no API Keys."} />
          </Then>
          <Else>
            <>
              <TableWrapper<Partial<ApiKey>>
                loading={loading}
                data={data.filter(isNotNil) as ApiKey[]}
                columns={apiKeysColumns({ refetchApiKeys })}
                rowSelection={rowSelection}
                setRowSelection={setRowSelection}
                stripped
              />
              <Pagination count={count} />
            </>
          </Else>
        </If>
      </LoaderWrap>
    </>
  );
}
